import React, { useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Minus, Plus, Check, Flame } from "lucide-react";
import { FLAVORS, FLAVOR_DETAILS, RECIPES, ASSETS, FLAVOR_IMAGES, SHOP_OPEN } from "../lib/content";
import { useCart } from "../lib/cart";
import { ShareButton } from "../components/ShareButton";

export default function FlavorPage({ onJoin }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qty, setQtyLocal] = useState(1);
  const { add, setOpen } = useCart();

  const flavor = FLAVORS.find((f) => f.id === id);
  if (!flavor) return <Navigate to="/" replace />;

  const details = FLAVOR_DETAILS[id] || {};
  const recipes = RECIPES.filter((r) => r.flavorId === id);
  const idx = FLAVORS.findIndex((f) => f.id === id);
  const prev = FLAVORS[(idx - 1 + FLAVORS.length) % FLAVORS.length];
  const next = FLAVORS[(idx + 1) % FLAVORS.length];

  const addToBag = () => {
    if (!SHOP_OPEN) {
      toast(`${flavor.name} drops soon. Join the Coffee Club for first dibs.`);
      onJoin();
      return;
    }
    add(flavor, qty);
    toast.success(`${qty} × ${flavor.name} added to your bag.`);
    setOpen(true);
  };

  return (
    <main data-testid="flavor-page" className="pt-28 pb-24 px-5 md:px-10">
      <div className="max-w-[1200px] mx-auto">
        <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/"))} data-testid="flavor-back-link" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-cream/65 hover:text-ember transition-colors">
          <ArrowLeft size={14} /> All flavors
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mt-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-lg overflow-hidden border border-white/10 bg-void min-h-[380px] md:min-h-[520px] lg:sticky lg:top-28 self-start"
          >
            <img src={FLAVOR_IMAGES[flavor.id] || ASSETS.packaging} alt={flavor.name} className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute top-0 inset-x-0 h-2" style={{ background: flavor.accent }} />
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 h-1.5 w-16 rounded-full" style={{ background: flavor.accent }} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex flex-wrap items-center gap-3">
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember">{details.roast || "Air roasted"}</p>
              <span data-testid="flavor-page-freshness-badge" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest bg-ash border border-ember/40 text-ember px-3 py-1.5 rounded-full">
                <Flame size={11} /> Air roasted this week · small batch
              </span>
            </div>
            <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl lg:text-6xl leading-[0.92] mt-3" data-testid="flavor-page-name">
              {flavor.name}
            </h1>
            <p className="font-mono text-sm text-cream/75 mt-4 leading-relaxed">{flavor.tagline}</p>

            <div className="flex flex-wrap items-center gap-3 mt-5">
              {flavor.notes.map((n) => (
                <span key={n} className="font-mono text-[10px] uppercase tracking-wider text-cream/70 border border-white/15 rounded-full px-2.5 py-1">{n}</span>
              ))}
              <ShareButton kind="flavor" slug={flavor.id} title={flavor.name} compact />
            </div>

            <div className="mt-8 border border-white/10 rounded-lg p-5 sm:p-6 bg-ash">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="font-mono">
                  <p className="text-cream text-2xl font-medium">$19.99</p>
                  <p className="text-ember text-xs mt-1">3+ bags · $17.99 each</p>
                  <p className="text-cream/55 text-[10px] mt-1">12 oz whole bean · ships across North America</p>
                </div>
                {SHOP_OPEN && (
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQtyLocal(Math.max(1, qty - 1))} data-testid="flavor-qty-dec" className="w-9 h-9 rounded-full border border-white/15 text-cream/70 hover:border-ember hover:text-ember transition-colors flex items-center justify-center"><Minus size={14} /></button>
                    <span className="font-mono text-lg text-cream w-6 text-center" data-testid="flavor-qty">{qty}</span>
                    <button onClick={() => setQtyLocal(qty + 1)} data-testid="flavor-qty-inc" className="w-9 h-9 rounded-full border border-white/15 text-cream/70 hover:border-ember hover:text-ember transition-colors flex items-center justify-center"><Plus size={14} /></button>
                  </div>
                )}
              </div>
              <button
                onClick={addToBag}
                data-testid="flavor-page-add-button"
                className="mt-5 w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-4 rounded-full font-medium hover:bg-emberDark transition-colors"
              >
                {SHOP_OPEN ? `Add to bag · $${(qty * (qty >= 3 ? 17.99 : 19.99)).toFixed(2)}` : "Dropping soon. Get first dibs"}
              </button>
            </div>

            {details.story && (
              <div className="mt-10">
                <h2 className="font-display font-extrabold uppercase tracking-tight text-cream text-lg">The story</h2>
                <p className="font-mono text-sm text-cream/75 mt-3 leading-relaxed">{details.story}</p>
              </div>
            )}

            {details.brew && (
              <div className="mt-8">
                <h2 className="font-display font-extrabold uppercase tracking-tight text-cream text-lg">Brew it right</h2>
                <ul className="mt-3 space-y-2.5">
                  {details.brew.map((b) => (
                    <li key={b} className="flex gap-3 font-mono text-sm text-cream/75 leading-relaxed">
                      <Check size={15} className="text-ember shrink-0 mt-0.5" /> {b}
                    </li>
                  ))}
                  <li className="flex gap-3 font-mono text-sm text-ember leading-relaxed" data-testid="zero-waste-tip">
                    <Check size={15} className="shrink-0 mt-0.5" /> Leftover pot? Pour it over ice tomorrow. Air-roasted coffee stays smooth overnight, so it makes excellent iced coffee the next day. Zero waste.
                  </li>
                </ul>
              </div>
            )}

            {details.pairing && (
              <p className="font-mono text-xs text-ember mt-8 border-l-2 border-ember pl-4 leading-relaxed">{details.pairing}</p>
            )}
          </motion.div>
        </div>

        {recipes.length > 0 && (
          <div className="mt-20">
            <h2 className="font-display font-black uppercase tracking-tight text-cream text-3xl md:text-4xl">
              Make trouble <span className="font-serif italic font-normal lowercase text-ember">with it</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
              {recipes.map((r) => (
                <Link
                  key={r.id}
                  to="/recipes"
                  data-testid={`flavor-recipe-${r.id}`}
                  className="group border border-white/10 rounded-lg p-6 bg-ash hover:border-ember/60 transition-colors"
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest bg-ember text-void px-2.5 py-1 rounded-full">{r.type}</span>
                  <h3 className="font-display font-extrabold uppercase tracking-tight text-cream text-xl mt-4 group-hover:text-ember transition-colors">{r.name}</h3>
                  <p className="font-mono text-xs text-cream/70 mt-2 leading-relaxed">{r.note}</p>
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ember mt-4">Full recipe <ArrowRight size={13} /></span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 flex items-center justify-between border-t border-white/10 pt-8">
          <Link to={`/flavor/${prev.id}`} data-testid="flavor-prev-link" className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-cream/75 hover:text-ember transition-colors">
            <ArrowLeft size={14} /> {prev.name}
          </Link>
          <Link to={`/flavor/${next.id}`} data-testid="flavor-next-link" className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-cream/75 hover:text-ember transition-colors">
            {next.name} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </main>
  );
}
